import { useEffect, useState } from "react";
import { Plus, Trash2, CheckCircle2, X, Globe, Loader2, BookOpen, Coins, ShieldCheck, Users as UsersIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { platformMeta } from "@/lib/format";

const CONF_BADGE = {
  CONFIRMED: "bg-[#122A1E] text-[#4ADE80] border-[#1B4D36]",
  PROBABLE: "bg-[#2A2312] text-[#FACC15] border-[#4D3E1B]",
  POSSIBLE: "bg-[#1A202C] text-[#94A3B8] border-[#334155]",
};

export default function Admin() {
  const [tab, setTab] = useState("sources");
  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8833A] mb-2">
          Settings
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#F3EFE6]">
          Interview Intelligence
        </h1>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-[#262933]">
        {[
          { k: "sources", l: "Sources" },
          { k: "registry", l: "Source Registry" },
          { k: "accounts", l: "Account Resolution" },
          { k: "users", l: "Users & Billing" },
          { k: "usage", l: "Usage & Credits" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={cn(
              "px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 -mb-px",
              tab === t.k ? "border-[#E8833A] text-[#F3EFE6]" : "border-transparent text-[#9A9CA6] hover:text-[#F3EFE6]"
            )}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "sources" && <SourcesToggle />}
      {tab === "registry" && <SourceRegistry />}
      {tab === "accounts" && <AccountResolution />}
      {tab === "users" && <UsersBilling />}
      {tab === "usage" && <UsageDashboard />}
    </div>
  );
}

const SourcesToggle = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.getSettings().then(setSettings);
  }, []);

  const toggle = async (p) => {
    const next = { ...settings.enabled_platforms, [p]: !settings.enabled_platforms[p] };
    setSettings({ ...settings, enabled_platforms: next });
    await api.updateSettings(next);
    toast.success(`${platformMeta(p).label} ${next[p] ? "enabled" : "disabled"}`);
  };

  const toggleDaily = async () => {
    const next = !settings.daily_refresh;
    setSettings({ ...settings, daily_refresh: next });
    await api.updateDaily(next);
    toast.success(`Daily refresh ${next ? "enabled" : "disabled"}`);
  };

  const [importingMG, setImportingMG] = useState(false);
  const importMG = async () => {
    setImportingMG(true);
    try {
      const r = await api.mediaGuideImport();
      toast.success(`Media Guide: ${r.matched} athletes updated · ${r.events_added} items`);
    } catch {
      toast.error("Media Guide import failed");
    }
    setImportingMG(false);
  };

  const [syncingPrca, setSyncingPrca] = useState(false);
  const syncPrca = async () => {
    setSyncingPrca(true);
    try {
      const r = await api.seedPrca();
      toast.success(`PRCA: ${r.added} new athletes added · ${r.updated} merged (dual-carded)`);
    } catch {
      toast.error("PRCA sync failed");
    }
    setSyncingPrca(false);
  };

  const [syncingNra, setSyncingNra] = useState(false);
  const syncNra = async () => {
    setSyncingNra(true);
    try {
      const r = await api.seedNra();
      toast.success(`NRA: ${r.added} contestants imported · ${r.notes_added} announcer bios added`);
    } catch {
      toast.error("NRA import failed");
    }
    setSyncingNra(false);
  };

  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState("");
  const backfillBriefs = async () => {
    setBackfilling(true);
    setBackfillMsg("");
    try {
      const r = await api.briefsBackfill();
      if (r.status === "nothing_to_do") {
        toast.success("Every scanned athlete already has a presenter brief");
        setBackfilling(false);
        return;
      }
      toast.info(`Building ${r.missing} missing briefs in the background…`);
      const poll = setInterval(async () => {
        const s = await api.briefsBackfillStatus();
        setBackfillMsg(`${s.done}/${s.total} processed · ${s.built} briefs built`);
        if (!s.running) {
          clearInterval(poll);
          setBackfilling(false);
          toast.success(`Done — ${s.built} presenter briefs built`);
        }
      }, 2500);
    } catch {
      toast.error("Backfill failed");
      setBackfilling(false);
    }
  };

  const [syncingWpra, setSyncingWpra] = useState(false);
  const syncWpra = async () => {
    setSyncingWpra(true);
    try {
      const r = await api.seedWpra();
      toast.success(`WPRA: ${r.added} new cowgirls added · ${r.updated} merged (dual-carded)`);
    } catch {
      toast.error("WPRA sync failed");
    }
    setSyncingWpra(false);
  };

  if (!settings) return <div className="text-[#686B77] py-10">Loading…</div>;

  return (
    <div>
      {/* Media guide import */}
      <div data-testid="media-guide-card" className="flex items-center justify-between rounded-xl border border-[#262933] bg-[#12141A] px-5 py-4 mb-4">
        <div>
          <div className="font-display font-bold text-[#F3EFE6]">2025 Media Guide Bios</div>
          <div className="text-sm text-[#9A9CA6] mt-0.5 max-w-xl">
            Attach official CPRA media-guide bios (career highlights, family, interests, quals) to
            each athlete as a "Media Guide" feed.
          </div>
        </div>
        <button
          data-testid="import-media-guide-button"
          onClick={importMG}
          disabled={importingMG}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60 shrink-0"
        >
          {importingMG ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
          Import Media Guide
        </button>
      </div>

      {/* PRCA roster sync */}
      <div data-testid="prca-sync-card" className="flex items-center justify-between rounded-xl border border-[#262933] bg-[#12141A] px-5 py-4 mb-4">
        <div>
          <div className="font-display font-bold text-[#F3EFE6]">PRCA World Standings</div>
          <div className="text-sm text-[#9A9CA6] mt-0.5 max-w-xl">
            Pull the top 15 in every PRCA event (plus All-Around) into the roster so you can
            search and scan them. Dual-carded cowboys (also in CPRA) are merged, not duplicated.
          </div>
        </div>
        <button
          data-testid="sync-prca-button"
          onClick={syncPrca}
          disabled={syncingPrca}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60 shrink-0"
        >
          {syncingPrca ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          Sync PRCA standings
        </button>
      </div>

      {/* WPRA roster sync */}
      <div data-testid="wpra-sync-card" className="flex items-center justify-between rounded-xl border border-[#262933] bg-[#12141A] px-5 py-4 mb-4">
        <div>
          <div className="font-display font-bold text-[#F3EFE6]">WPRA Women's Standings</div>
          <div className="text-sm text-[#9A9CA6] mt-0.5 max-w-xl">
            Pull the top 30 cowgirls in the WPRA Playoff Series — Barrel Racing and Breakaway Roping — from wpra.com.
            Cowgirls already carded in PRCA barrel racing are merged, not duplicated.
          </div>
        </div>
        <button
          data-testid="sync-wpra-button"
          onClick={syncWpra}
          disabled={syncingWpra}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60 shrink-0"
        >
          {syncingWpra ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          Sync WPRA standings
        </button>
      </div>

      {/* NRA roster import */}
      <div data-testid="nra-sync-card" className="flex items-center justify-between rounded-xl border border-[#262933] bg-[#12141A] px-5 py-4 mb-4">
        <div>
          <div className="font-display font-bold text-[#F3EFE6]">NRA Contestants (with bios)</div>
          <div className="text-sm text-[#9A9CA6] mt-0.5 max-w-xl">
            Import the Northern Rodeo Association roster from the uploaded announcer-bios sheet.
            Each contestant's supplied bio is saved as an announcer note; they can still be web-scanned
            for socials like everyone else. Kept in their own NRA circuit.
          </div>
        </div>
        <button
          data-testid="sync-nra-button"
          onClick={syncNra}
          disabled={syncingNra}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60 shrink-0"
        >
          {syncingNra ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
          Import NRA contestants
        </button>
      </div>

      {/* Presenter brief backfill */}
      <div data-testid="brief-backfill-card" className="flex items-center justify-between rounded-xl border border-[#262933] bg-[#12141A] px-5 py-4 mb-4">
        <div>
          <div className="font-display font-bold text-[#F3EFE6]">Presenter Briefs</div>
          <div className="text-sm text-[#9A9CA6] mt-0.5 max-w-xl">
            A presenter brief is written automatically at the end of every discovery. Use this to
            build briefs for any already-scanned athletes that are missing one — no credits charged.
            {backfillMsg && <span className="block mt-1 font-mono text-[11px] text-[#E8833A]">{backfillMsg}</span>}
          </div>
        </div>
        <button
          data-testid="backfill-briefs-button"
          onClick={backfillBriefs}
          disabled={backfilling}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60 shrink-0"
        >
          {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Backfill missing briefs
        </button>
      </div>

      {/* Daily refresh */}
      <div data-testid="daily-refresh-card" className="flex items-center justify-between rounded-xl border border-[#E8833A]/30 bg-[#12141A] px-5 py-4 mb-6">
        <div>
          <div className="font-display font-bold text-[#F3EFE6]">Daily Refresh</div>
          <div className="text-sm text-[#9A9CA6] mt-0.5 max-w-xl">
            Automatically re-scan every athlete once a day so their briefs stay current between rodeos.
          </div>
        </div>
        <Switch checked={!!settings.daily_refresh} onCheckedChange={toggleDaily} data-testid="switch-daily-refresh" />
      </div>

      <p className="text-sm text-[#9A9CA6] mb-5 max-w-2xl">
        Enable or disable which public source types the discovery engine scans. Disabled
        platforms are skipped for every athlete on the next run.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {settings.platforms.map((p) => {
          const m = platformMeta(p);
          const Icon = m.icon;
          const on = settings.enabled_platforms[p];
          return (
            <div
              key={p}
              data-testid={`source-toggle-${p}`}
              className="flex items-center justify-between rounded-xl border border-[#262933] bg-[#12141A] px-4 py-3.5"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg bg-[#1A1D26] border border-[#262933] flex items-center justify-center" style={{ color: m.color }}>
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <span className="text-[#F3EFE6] font-medium">{m.label}</span>
              </div>
              <Switch checked={on} onCheckedChange={() => toggle(p)} data-testid={`switch-${p}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EMPTY_SRC = { name: "", url: "", country: "", association: "", category: "Rodeo Organization", priority: 3 };

const SourceRegistry = () => {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(EMPTY_SRC);
  const [adding, setAdding] = useState(false);

  const load = () => api.listSources().then(setSources);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.url) return toast.error("Name and URL are required");
    await api.createSource(form);
    toast.success("Source added");
    setForm(EMPTY_SRC);
    setAdding(false);
    load();
  };

  const del = async (id) => {
    await api.deleteSource(id);
    toast.success("Source removed");
    load();
  };

  const toggleActive = async (s) => {
    await api.updateSource(s.id, { active: !s.active });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#9A9CA6] max-w-xl">
          Manage websites, rodeo organizations, groups, media outlets, podcasts and sponsors
          the engine treats as trusted sources.
        </p>
        <button
          data-testid="add-source-button"
          onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors shrink-0"
        >
          <Plus className="h-4 w-4" /> Add source
        </button>
      </div>

      {adding && (
        <div data-testid="add-source-form" className="rounded-xl border border-[#E8833A]/30 bg-[#12141A] p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["name", "Source name"],
            ["url", "URL / domain"],
            ["country", "Country"],
            ["association", "Association"],
            ["category", "Category"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">{label}</label>
              <Input
                data-testid={`source-input-${k}`}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 bg-[#0F1116] border-[#262933] text-[#F3EFE6] focus-visible:ring-[#E8833A]"
              />
            </div>
          ))}
          <div className="flex items-end gap-2">
            <button data-testid="save-source-button" onClick={add} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors">
              Save
            </button>
            <button onClick={() => { setAdding(false); setForm(EMPTY_SRC); }} className="px-4 py-2 rounded-lg text-sm text-[#9A9CA6] hover:text-[#F3EFE6] transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[#262933] bg-[#12141A] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#262933] text-left font-mono text-[10px] uppercase tracking-widest text-[#686B77]">
              <th className="px-5 py-3">Source</th>
              <th className="px-5 py-3 hidden md:table-cell">Category</th>
              <th className="px-5 py-3 hidden lg:table-cell">Country</th>
              <th className="px-5 py-3">Active</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} data-testid="source-row" className="border-b border-[#1A1D26] last:border-0">
                <td className="px-5 py-3">
                  <div className="text-[#F3EFE6] font-medium flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5 text-[#686B77]" /> {s.name}
                  </div>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7FB2E0] hover:underline">{s.url}</a>
                </td>
                <td className="px-5 py-3 hidden md:table-cell text-[#9A9CA6]">{s.category}</td>
                <td className="px-5 py-3 hidden lg:table-cell text-[#9A9CA6]">{s.country || "—"}</td>
                <td className="px-5 py-3">
                  <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`source-active-${s.id}`} />
                </td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`delete-source-${s.id}`} onClick={() => del(s.id)} className="text-[#686B77] hover:text-[#FB923C] transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AccountResolution = () => {
  const [accounts, setAccounts] = useState([]);

  const load = () => api.allAccounts(true).then(setAccounts);
  useEffect(() => { load(); }, []);

  const act = async (id, body) => {
    await api.updateAccount(id, body);
    toast.success("Updated");
    load();
  };

  if (accounts.length === 0) {
    return (
      <div className="text-center py-20 rounded-xl border border-dashed border-[#262933] bg-[#0F1116] text-[#9A9CA6]">
        No accounts pending review. Discovered accounts awaiting confirmation appear here.
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-[#9A9CA6] mb-5 max-w-2xl">
        Discovered social accounts awaiting manual verification. Only CONFIRMED and manually
        approved accounts feed the interview intelligence.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.map((acc) => {
          const m = platformMeta(acc.platform);
          const Icon = m.icon;
          return (
            <div key={acc.id} data-testid="pending-account-card" className="rounded-xl border border-[#262933] bg-[#12141A] p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="h-9 w-9 rounded-lg bg-[#1A1D26] border border-[#262933] flex items-center justify-center" style={{ color: m.color }}>
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[#F3EFE6] font-medium">{acc.athlete_name}</div>
                    <a href={acc.profile_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7FB2E0] hover:underline truncate block max-w-[200px]">
                      {m.label} · {acc.profile_url}
                    </a>
                  </div>
                </div>
                <span className={cn("px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-wider font-semibold", CONF_BADGE[acc.confidence])}>
                  {acc.confidence}
                </span>
              </div>
              {acc.evidence && <p className="text-xs text-[#9A9CA6] mb-3 leading-relaxed">{acc.evidence}</p>}
              <div className="flex items-center gap-2">
                <button data-testid="confirm-account-button" onClick={() => act(acc.id, { manually_verified: true, rejected: false })} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#122A1E] text-[#4ADE80] border border-[#1B4D36] hover:bg-[#16341f] transition-colors">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Confirm
                </button>
                <button data-testid="reject-account-button" onClick={() => act(acc.id, { rejected: true, manually_verified: false })} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#2A1812] text-[#FB923C] border border-[#4D261B] hover:bg-[#331d15] transition-colors">
                  <X className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LEDGER_LABEL = {
  scan: "New scan", refresh: "Refresh", scan_refund: "Scan refund", refresh_refund: "Refresh refund",
  subscription: "Subscription", topup: "Top-up", admin_grant: "Admin grant", admin_set: "Admin set",
};

const UsersBilling = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [addAmts, setAddAmts] = useState({});

  const load = () => { setLoading(true); api.adminUsers().then((u) => { setUsers(u); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const toggleComp = async (u) => {
    await api.adminPatchUser(u.id, { comped: !u.comped });
    toast.success(`${u.email} ${!u.comped ? "comped" : "un-comped"}`);
    load();
  };
  const addCredits = async (u) => {
    const amt = parseInt(addAmts[u.id], 10);
    if (!amt) return toast.error("Enter a credit amount");
    await api.adminPatchUser(u.id, { add_credits: amt });
    toast.success(`${amt > 0 ? "Added" : "Removed"} ${Math.abs(amt)} credits for ${u.email}`);
    setAddAmts({ ...addAmts, [u.id]: "" });
    load();
  };
  const openLedger = async (u) => {
    setLedgerFor(u); setLedger(null);
    const d = await api.adminUserLedger(u.id);
    setLedger(d.transactions);
  };

  if (loading) return <div className="text-[#686B77] py-10">Loading users…</div>;

  return (
    <div data-testid="users-billing">
      <p className="text-sm text-[#9A9CA6] mb-5 max-w-2xl">
        Every account, their credit wallet and usage history. <span className="text-[#F3EFE6]">Comp</span> a user and
        they can scan freely on account — each scan/refresh is tracked and the balance goes negative, showing what they
        <span className="text-[#F3EFE6]"> owe</span> you so you can bill them directly. Regular users must buy credits and are blocked at zero.
      </p>
      <div className="rounded-xl border border-[#262933] bg-[#12141A] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#262933] text-left font-mono text-[10px] uppercase tracking-widest text-[#686B77]">
              <th className="px-5 py-3">User</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Balance</th>
              <th className="px-5 py-3">Used</th>
              <th className="px-5 py-3">Owed</th>
              <th className="px-5 py-3">Comp</th>
              <th className="px-5 py-3">Add credits</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#1A1D26] last:border-0">
                <td className="px-5 py-3">
                  <div className="text-[#F3EFE6] font-medium flex items-center gap-2">
                    {u.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-[#4ADE80]" />}
                    {u.name || "—"}
                  </div>
                  <div className="text-xs text-[#686B77]">{u.email}</div>
                </td>
                <td className="px-5 py-3">
                  <span className={cn("px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-wider",
                    u.role === "admin" ? "bg-[#122A1E] text-[#4ADE80] border-[#1B4D36]"
                    : u.subscription_status === "active" ? "bg-[#2A2312] text-[#FACC15] border-[#4D3E1B]"
                    : "bg-[#1A202C] text-[#94A3B8] border-[#334155]")}>
                    {u.role === "admin" ? "Owner" : u.subscription_status === "active" ? "Pro" : u.comped ? "Comped" : "Free"}
                  </span>
                </td>
                <td className="px-5 py-3 font-mono font-semibold flex items-center gap-1">
                  <Coins className="h-3.5 w-3.5 text-[#E8833A]" />
                  <span className={u.role === "admin" ? "text-[#E8833A]" : u.credits < 0 ? "text-[#FB7185]" : "text-[#E8833A]"}>
                    {u.role === "admin" ? "∞" : u.credits}
                  </span>
                </td>
                <td className="px-5 py-3 font-mono text-[#9A9CA6]">{u.used_credits ?? u.spent_credits ?? 0}</td>
                <td className="px-5 py-3 font-mono">
                  {u.owed_credits ? (
                    <span className="text-[#FB7185] font-semibold" data-testid={`owed-${u.email}`}>{u.owed_credits} cr</span>
                  ) : <span className="text-[#686B77]">—</span>}
                </td>
                <td className="px-5 py-3">
                  {u.role === "admin" ? <span className="text-[#686B77] text-xs">—</span>
                    : <Switch checked={!!u.comped} onCheckedChange={() => toggleComp(u)} data-testid={`comp-switch-${u.email}`} />}
                </td>
                <td className="px-5 py-3">
                  {u.role !== "admin" && (
                    <div className="flex items-center gap-1.5">
                      <Input
                        data-testid={`add-credits-input-${u.email}`}
                        type="number"
                        value={addAmts[u.id] || ""}
                        onChange={(e) => setAddAmts({ ...addAmts, [u.id]: e.target.value })}
                        placeholder="±"
                        className="w-16 h-8 bg-[#0F1116] border-[#262933] text-[#F3EFE6] text-xs"
                      />
                      <button data-testid={`add-credits-btn-${u.email}`} onClick={() => addCredits(u)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors">
                        Apply
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`ledger-btn-${u.email}`} onClick={() => openLedger(u)}
                    className="text-xs text-[#7FB2E0] hover:underline">History</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ledgerFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setLedgerFor(null)}>
          <div data-testid="ledger-modal" className="w-full max-w-lg rounded-xl border border-[#262933] bg-[#12141A] p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display font-bold text-lg text-[#F3EFE6]">{ledgerFor.email}</div>
                <div className="text-xs text-[#686B77]">Refresh & credit history</div>
              </div>
              <button onClick={() => setLedgerFor(null)} className="text-[#686B77] hover:text-[#F3EFE6]"><X className="h-5 w-5" /></button>
            </div>
            {!ledger ? <div className="text-[#686B77] py-6">Loading…</div>
              : ledger.length === 0 ? <div className="text-[#686B77] py-6 text-sm">No transactions yet.</div>
              : (
                <div className="divide-y divide-[#1E212A]">
                  {ledger.map((t) => (
                    <div key={t.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <span className="text-[#F3EFE6]">{LEDGER_LABEL[t.action] || t.action}</span>
                        {t.athlete_name && <span className="text-[#9A9CA6]"> · {t.athlete_name}</span>}
                        <div className="font-mono text-[10px] text-[#686B77]">{(t.created_at || "").slice(0, 16).replace("T", " ")}{t.note ? ` · ${t.note}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 font-mono text-xs">
                        <span className={t.amount < 0 ? "text-[#FB923C]" : "text-[#4ADE80]"}>{t.amount > 0 ? `+${t.amount}` : t.amount}</span>
                        <span className="text-[#686B77]">bal {t.balance_after}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

const Metric = ({ label, value, highlight }) => (  <div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">{label}</div>
    <div className={cn("font-display font-extrabold text-2xl mt-0.5", highlight ? "text-[#4ADE80]" : "text-[#F3EFE6]")}>{value}</div>
  </div>
);

const UsageDashboard = () => {
  const [sum, setSum] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.usageSummary().then(setSum).catch(() => {});
    api.usageLog(100).then(setLog).catch(() => {});
  }, []);

  if (!sum) return <div className="text-[#686B77] py-10">Loading usage…</div>;

  const cards = [
    { label: "Athlete scans", value: sum.total_scans, testid: "usage-scans" },
    { label: "Credits used", value: sum.total_credits, testid: "usage-credits" },
    { label: "LLM calls", value: sum.total_llm_calls, testid: "usage-calls" },
    { label: "Tokens", value: (sum.total_tokens || 0).toLocaleString(), testid: "usage-tokens" },
  ];

  return (
    <div data-testid="usage-dashboard">
      <div className="mb-5">
        <div className="font-display font-bold text-[#F3EFE6] text-lg">System LLM Usage</div>
        <div className="text-sm text-[#9A9CA6] mt-0.5">
          {sum.provider} · {sum.model} · 1 credit = 1 athlete scan (3-pass deep scan + presenter brief)
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-xl border border-[#262933] bg-[#12141A] p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">{c.label}</div>
            <div className="font-display font-extrabold text-3xl text-[#E8833A] mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      {sum.total_scans > 0 && (
        <div className="rounded-xl border border-[#E8833A]/30 bg-[#E8833A]/5 p-5 mb-6" data-testid="usage-pricing">
          <div className="font-display font-bold text-[#F3EFE6] mb-3">What a scan actually costs you</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="Avg tokens / scan" value={(sum.avg_tokens_per_scan || 0).toLocaleString()} />
            <Metric label="Avg cost / scan" value={`$${sum.avg_cost_per_scan_usd ?? 0}`} />
            <Metric label="Platform credits / scan" value={sum.avg_platform_credits_per_scan ?? 0} />
            <Metric label="Suggested charge / scan" value={sum.suggested_charge_per_scan_usd ? `$${sum.suggested_charge_per_scan_usd}` : "—"} highlight />
          </div>
          <p className="text-[11px] text-[#686B77] mt-3 leading-relaxed">
            Cost = Gemini 3 Pro tokens ($2/1M in, $12/1M out) + Google Search grounding ($0.014/pass).
            "Suggested charge" is ~3× your cost — adjust to your margin. Based on {sum.total_scans} scan{sum.total_scans === 1 ? "" : "s"} so far.
          </p>
        </div>
      )}

      {sum.total_tokens === 0 && sum.total_scans > 0 && (
        <p className="text-[11px] text-[#686B77] mb-5">
          Token counts aren't exposed by the provider for grounded-search calls, so billing is metered per scan (the stable unit).
        </p>
      )}

      <div className="rounded-xl border border-[#262933] bg-[#12141A] overflow-hidden">
        <div className="px-5 py-3 border-b border-[#262933] font-mono text-[10px] uppercase tracking-widest text-[#686B77]">
          Recent activity
        </div>
        {log.length === 0 ? (
          <div className="px-5 py-8 text-sm text-[#686B77]">No usage recorded yet. Run a scan to see it here.</div>
        ) : (
          <div className="divide-y divide-[#1E212A]">
            {log.map((d) => (
              <div key={d.id} data-testid="usage-row" className="px-5 py-3 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className="text-[#F3EFE6]">{d.athlete_name || d.action}</span>
                  <span className="font-mono text-[11px] text-[#686B77] ml-2">{d.action}</span>
                </div>
                <div className="flex items-center gap-4 shrink-0 font-mono text-[11px] text-[#9A9CA6]">
                  <span>{d.llm_calls} calls</span>
                  <span className="text-[#E8833A]">{d.credits} cr</span>
                  <span>{(d.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
